// Schema fingerprint groups in v2 Data section.
//
// Renders the auto-suggested groups returned by /views:auto-suggest with:
//   - per-group unique color derived deterministically from group_id so
//     the same group keeps the same color across refreshes
//   - count + field-list preview
//   - "✨ Name them with AI" action — calls /views:auto-name and
//     overlays Gemini-generated names on top of the default Schema·N
//     placeholders
//
// Backlog item: per-group "Save as view" CTA that opens SaveViewDialog
// with the group's filter pre-filled. Tracked in BACKLOG.md (not yet
// wired here).

import { useEffect, useState } from 'react';
import type { Source } from '@datahub/shared';
import { getViewSuggestions, autoNameViewGroups, saveSchemaDef, archiveSchemaDef, promoteSchemaToBQ, proposeSchemaMerges, getSchemaDriftFeed, patchSchemaDef, type ViewSuggestion, type NamedSchemaGroup, type MergeProposal, type DriftReport } from '../../api';
import { useToast } from '../Toast';
import { EmptyState } from './SectionShell';
import { TagChip } from '../../lib/tag-colors';

interface Props {
  source: Source;
}

/** 12 well-spaced hues that keep enough contrast against both light
 *  and dark themes via oklch lightness 0.55. Picked from a 30-degree
 *  rotation around the chroma circle. */
const PALETTE = [
  { name: 'rose',    bg: 'oklch(0.96 0.04 25 / 0.7)',  fg: 'oklch(0.50 0.18 25)',  border: 'oklch(0.85 0.10 25)' },
  { name: 'orange',  bg: 'oklch(0.96 0.04 60 / 0.7)',  fg: 'oklch(0.50 0.16 60)',  border: 'oklch(0.85 0.10 60)' },
  { name: 'amber',   bg: 'oklch(0.96 0.04 90 / 0.7)',  fg: 'oklch(0.50 0.14 90)',  border: 'oklch(0.85 0.10 90)' },
  { name: 'lime',    bg: 'oklch(0.96 0.04 130 / 0.7)', fg: 'oklch(0.45 0.16 130)', border: 'oklch(0.85 0.10 130)' },
  { name: 'green',   bg: 'oklch(0.96 0.04 150 / 0.7)', fg: 'oklch(0.45 0.16 150)', border: 'oklch(0.85 0.10 150)' },
  { name: 'teal',    bg: 'oklch(0.96 0.04 180 / 0.7)', fg: 'oklch(0.45 0.14 180)', border: 'oklch(0.85 0.10 180)' },
  { name: 'cyan',    bg: 'oklch(0.96 0.04 210 / 0.7)', fg: 'oklch(0.45 0.14 210)', border: 'oklch(0.85 0.10 210)' },
  { name: 'blue',    bg: 'oklch(0.96 0.04 240 / 0.7)', fg: 'oklch(0.45 0.16 240)', border: 'oklch(0.85 0.10 240)' },
  { name: 'indigo',  bg: 'oklch(0.96 0.04 270 / 0.7)', fg: 'oklch(0.45 0.16 270)', border: 'oklch(0.85 0.10 270)' },
  { name: 'violet',  bg: 'oklch(0.96 0.04 290 / 0.7)', fg: 'oklch(0.50 0.16 290)', border: 'oklch(0.85 0.10 290)' },
  { name: 'magenta', bg: 'oklch(0.96 0.04 320 / 0.7)', fg: 'oklch(0.50 0.18 320)', border: 'oklch(0.85 0.10 320)' },
  { name: 'pink',    bg: 'oklch(0.96 0.04 350 / 0.7)', fg: 'oklch(0.50 0.18 350)', border: 'oklch(0.85 0.10 350)' },
] as const;

/** Hash a group id deterministically into a palette index so the same
 *  group keeps the same color across page reloads. Operator's mental
 *  map "the deal-update group is always blue" stays stable. */
function colorForId(id: string): typeof PALETTE[number] {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

/** Focus mode: ONE group is the active subject; operator can toggle
 *  other groups (whole) or specific variants (per-block) into a live
 *  comparison matrix anchored on the focused group's variants.
 *
 *  Layout:
 *   - Header strip: chips for every group. Active gets a thick color
 *     border; others toggle into compareGroupIds when clicked.
 *   - Active card: full detail, prominent.
 *   - Compare panel: matrix of paths × (active group's variants +
 *     selected compared groups' variants + individually-pinned variants)
 *   - Per-active-variant + per-compared-variant individual toggles
 *     control which columns participate.
 */
function FocusModeView({
  suggestions,
  aiNames,
  focusedGroupId,
  compareGroupIds,
  compareVariantIds,
  constantKeys,
  onSelectFocus,
  onToggleCompareGroup,
  onToggleCompareVariant,
  onExit,
}: {
  suggestions: ViewSuggestion[];
  aiNames: Record<string, NamedSchemaGroup>;
  focusedGroupId: string;
  compareGroupIds: Set<string>;
  compareVariantIds: Set<string>;
  constantKeys: Set<string>;
  onSelectFocus: (id: string) => void;
  onToggleCompareGroup: (id: string) => void;
  onToggleCompareVariant: (groupId: string, variantId: string) => void;
  onExit: () => void;
}): React.ReactElement {
  const focused = suggestions.find(s => s.id === focusedGroupId);
  if (!focused) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
        Focused group not in current suggestions.
        <button onClick={onExit} className="btn sm ghost" style={{ marginLeft: 8 }}>exit focus</button>
      </div>
    );
  }
  const focusedColor = colorForId(focused.id);
  const focusedName = aiNames[focused.id]?.name ?? focused.matched_schema_name ?? focused.name;

  // Build matrix columns:
  // - all variants of the active group (always included unless explicitly toggled OFF)
  // - all variants of any group in compareGroupIds
  // - individually-pinned variants from compareVariantIds (groupId::variantId)
  type Col = {
    variant_id: string;
    paths: Set<string>;
    count: number;
    group_id: string;
    group_name: string;
    group_color: ReturnType<typeof colorForId>;
    is_focused: boolean;
    is_first_in_group: boolean;
  };
  const cols: Col[] = [];

  function pushVariants(s: ViewSuggestion, isFocused: boolean) {
    const variants = s.variants && s.variants.length > 0
      ? s.variants
      : [{ fingerprint_id: s.id, paths: s.paths ?? [], count: s.estimated_count }];
    const groupName = aiNames[s.id]?.name ?? s.matched_schema_name ?? s.name;
    const c = colorForId(s.id);
    variants.forEach((v, i) => {
      cols.push({
        variant_id: v.fingerprint_id,
        paths: new Set(v.paths),
        count: v.count,
        group_id: s.id,
        group_name: groupName,
        group_color: c,
        is_focused: isFocused,
        is_first_in_group: i === 0,
      });
    });
  }

  pushVariants(focused, true);
  for (const s of suggestions) {
    if (s.id === focusedGroupId) continue;
    if (compareGroupIds.has(s.id)) {
      pushVariants(s, false);
    } else {
      // Individually-pinned variants from this group?
      const variants = s.variants && s.variants.length > 0
        ? s.variants
        : [{ fingerprint_id: s.id, paths: s.paths ?? [], count: s.estimated_count }];
      const groupName = aiNames[s.id]?.name ?? s.matched_schema_name ?? s.name;
      const c = colorForId(s.id);
      variants.forEach((v, i) => {
        if (compareVariantIds.has(`${s.id}::${v.fingerprint_id}`)) {
          cols.push({
            variant_id: v.fingerprint_id,
            paths: new Set(v.paths),
            count: v.count,
            group_id: s.id,
            group_name: groupName,
            group_color: c,
            is_focused: false,
            is_first_in_group: i === 0,
          });
        }
      });
    }
  }

  // Path union + presence count for the matrix.
  const allSet = new Set<string>();
  for (const c of cols) for (const p of c.paths) allSet.add(p);
  const all = [...allSet].sort();
  const havingCount: Record<string, number> = {};
  for (const p of all) havingCount[p] = cols.reduce((n, c) => n + (c.paths.has(p) ? 1 : 0), 0);
  const distinguishing = all.filter(p => havingCount[p]! < cols.length);
  const shared = all.filter(p => havingCount[p] === cols.length);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header — focus indicator + exit + group chips */}
      <div style={{
        padding: '10px 12px',
        background: 'var(--bg-sunken)',
        border: `1px solid ${focusedColor.border}`,
        borderLeft: `4px solid ${focusedColor.fg}`,
        borderRadius: 6,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
            focused
          </span>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: focusedColor.fg, flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
            {focusedName}
          </span>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
            {focused.estimated_count} ev · {focused.variants?.length ?? 1} variant{(focused.variants?.length ?? 1) === 1 ? '' : 's'}
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn sm ghost" onClick={onExit}>← exit focus</button>
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginRight: 4 }}>
            switch / compare
          </span>
          {suggestions.map(s => {
            const c = colorForId(s.id);
            const isActive = s.id === focusedGroupId;
            const isCompared = compareGroupIds.has(s.id);
            const name = aiNames[s.id]?.name ?? s.matched_schema_name ?? s.name;
            return (
              <span key={s.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 6px', fontSize: 11, borderRadius: 4,
                border: `${isActive ? 2 : 1}px solid ${isActive ? c.fg : c.border}`,
                background: isActive ? c.bg : (isCompared ? c.bg : 'var(--bg)'),
                color: c.fg,
              }}>
                <button
                  onClick={() => onSelectFocus(s.id)}
                  title={isActive ? 'Active focus' : 'Make this group the active focus'}
                  style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontFamily: 'var(--mono)', fontSize: 11, fontWeight: isActive ? 600 : 400 }}
                >
                  {isActive ? '◉' : '○'} {name} <span style={{ opacity: 0.7 }}>({s.estimated_count})</span>
                </button>
                {!isActive && (
                  <button
                    onClick={() => onToggleCompareGroup(s.id)}
                    title={isCompared ? 'Remove from compare' : 'Add whole group to compare'}
                    style={{ marginLeft: 4, background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 12 }}
                  >{isCompared ? '✓' : '+'}</button>
                )}
              </span>
            );
          })}
        </div>
      </div>

      {/* Active group's full detail block, plus per-variant toggle row */}
      <FocusActiveCard
        suggestion={focused}
        aiName={aiNames[focused.id]}
        constantKeys={constantKeys}
        compareVariantIds={compareVariantIds}
        onToggleCompareVariant={onToggleCompareVariant}
      />

      {/* Comparison matrix */}
      <div style={{
        padding: 14,
        background: 'var(--bg-elev)',
        border: '1px solid var(--accent-line)',
        borderRadius: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 600 }}>
            comparison matrix · {cols.length} variant{cols.length === 1 ? '' : 's'}
          </span>
          <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
            {distinguishing.length} distinguishing · {shared.length} shared
          </span>
        </div>
        {cols.length === 1 ? (
          <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
            Only the focused group's single variant is in the matrix — toggle other groups (chips above) or individual variants (below the active card) to compare.
          </div>
        ) : (
          <FocusMatrix cols={cols} all={all} havingCount={havingCount} />
        )}
      </div>
    </div>
  );
}

function FocusActiveCard({
  suggestion: s,
  aiName,
  constantKeys,
  compareVariantIds,
  onToggleCompareVariant,
}: {
  suggestion: ViewSuggestion;
  aiName?: NamedSchemaGroup;
  constantKeys: Set<string>;
  compareVariantIds: Set<string>;
  onToggleCompareVariant: (groupId: string, variantId: string) => void;
}): React.ReactElement {
  const color = colorForId(s.id);
  const variants = s.variants && s.variants.length > 0
    ? s.variants
    : [{ fingerprint_id: s.id, paths: s.paths ?? [], count: s.estimated_count }];
  return (
    <div style={{
      padding: 12,
      background: 'var(--bg)',
      border: `1px solid ${color.border}`,
      borderLeft: `4px solid ${color.fg}`,
      borderRadius: 6,
    }}>
      {aiName?.description && (
        <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 8 }}>
          {aiName.description}
        </div>
      )}
      {aiName?.tags && Object.entries(aiName.tags).filter(([k]) => !constantKeys.has(k)).length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
          {Object.entries(aiName.tags)
            .filter(([k]) => !constantKeys.has(k))
            .map(([k, v]) => <TagChip key={k} k={k} v={v} />)}
        </div>
      )}
      {variants.length > 1 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 4 }}>
            variants in this group
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {variants.map(v => (
              <span key={v.fingerprint_id} title={`${v.fingerprint_id} · ${v.count} ev · ${v.paths.length} paths`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 8px', fontSize: 10.5, fontFamily: 'var(--mono)',
                      background: color.bg, color: color.fg,
                      border: `1px solid ${color.border}`, borderRadius: 4,
                    }}>
                {v.fingerprint_id.replace('grp_', '').slice(0, 8)} · {v.count} ev · {v.paths.length}f
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', marginTop: 8 }}>
        {(s.core_paths?.length ?? 0)} core · {(s.optional_paths?.length ?? 0)} optional
        {s.origin_summary && <> · {s.origin_summary}</>}
        {s.matched_schema_id && <> · ★ saved schema</>}
      </div>
      {/* Per-variant pin toggles for adding to comparison */}
      {variants.length > 1 && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--line)' }}>
          <div style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 4 }}>
            pin specific variants only
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {variants.map(v => {
              const pinned = compareVariantIds.has(`${s.id}::${v.fingerprint_id}`);
              return (
                <button key={v.fingerprint_id}
                        onClick={() => onToggleCompareVariant(s.id, v.fingerprint_id)}
                        title={pinned ? 'Remove this variant from compare' : 'Add this variant to compare'}
                        style={{
                          padding: '2px 8px', fontSize: 10.5, fontFamily: 'var(--mono)',
                          background: pinned ? color.bg : 'transparent',
                          color: pinned ? color.fg : 'var(--ink-3)',
                          border: `1px solid ${pinned ? color.border : 'var(--line)'}`,
                          borderRadius: 3, cursor: 'pointer',
                        }}>
                  {pinned ? '✓' : '+'} {v.fingerprint_id.replace('grp_', '').slice(0, 8)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FocusMatrix({
  cols,
  all,
  havingCount,
}: {
  cols: Array<{
    variant_id: string;
    paths: Set<string>;
    count: number;
    group_id: string;
    group_name: string;
    group_color: ReturnType<typeof colorForId>;
    is_focused: boolean;
    is_first_in_group: boolean;
  }>;
  all: string[];
  havingCount: Record<string, number>;
}): React.ReactElement {
  const [showShared, setShowShared] = useState(false);
  const distinguishing = all.filter(p => havingCount[p]! < cols.length);
  const visible = showShared ? all : distinguishing;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <button className="btn sm ghost" onClick={() => setShowShared(s => !s)} style={{ fontSize: 10, padding: '2px 8px' }}>
          {showShared ? 'hide shared' : 'show all'}
        </button>
      </div>
      {visible.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          All compared variants share identical field-sets — differences are only in field VALUES.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 10.5 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 10px 4px 0', fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>field</th>
                {cols.map(c => (
                  <th key={c.variant_id}
                      title={`${c.group_name} · variant ${c.variant_id} · ${c.count} event${c.count === 1 ? '' : 's'}${c.is_focused ? ' · FOCUSED' : ''}`}
                      style={{
                        padding: '4px 6px',
                        minWidth: 60,
                        textAlign: 'center',
                        background: c.is_first_in_group ? c.group_color.bg : undefined,
                        borderLeft: c.is_first_in_group ? `2px solid ${c.group_color.fg}` : undefined,
                        verticalAlign: 'bottom',
                        fontWeight: c.is_focused ? 600 : 400,
                      }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: 9 }}>
                      <span style={{ color: c.group_color.fg, fontSize: c.is_focused ? 11 : 9 }}>
                        {c.is_focused ? '◉' : c.is_first_in_group ? '◀' : '·'}
                      </span>
                      <span style={{ color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{c.count}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(p => {
                const isShared = havingCount[p] === cols.length;
                return (
                  <tr key={p}>
                    <td style={{
                      padding: '2px 10px 2px 0',
                      fontFamily: 'var(--mono)', fontSize: 10.5,
                      color: isShared ? 'var(--ink-3)' : 'var(--ink-2)',
                      whiteSpace: 'nowrap',
                      fontWeight: isShared ? 400 : 500,
                    }}>{p}</td>
                    {cols.map(c => {
                      const has = c.paths.has(p);
                      return (
                        <td key={c.variant_id} style={{
                          padding: '2px 6px',
                          textAlign: 'center',
                          fontFamily: 'var(--mono)', fontSize: 11,
                          background: !isShared && has ? c.group_color.bg : undefined,
                          color: has ? c.group_color.fg : 'var(--ink-3)',
                          opacity: has ? 1 : 0.25,
                          fontWeight: !isShared && has ? 600 : 400,
                          borderLeft: c.is_first_in_group ? `2px solid ${c.group_color.fg}` : undefined,
                        }}>{has ? '✓' : '·'}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Full-screen grouping verification matrix. Lays out EVERY fingerprint
 *  variant across EVERY group in one giant table so operator can do
 *  a single visual scan and confirm "yes the auto-grouping is right".
 *
 *  Columns are sorted so same-group variants sit adjacent and share
 *  the group's color; different groups have visible boundaries. A
 *  same-group block of N adjacent columns should look "similar" in
 *  pattern (mostly same ✓ rows) — outlier columns inside a block hint
 *  at "this variant maybe shouldn't have been merged into this group".
 *
 *  Default mode hides path rows that ALL variants share (the spine);
 *  toggle reveals them. Operator can also toggle "highlight grouping
 *  boundaries" to make the visual blocks more obvious.
 */
/** T3.1 + 3.2 schema drift modal. Lists every drifted path for the
 *  selected schema with the same color treatment + frequency badge,
 *  and per-row controls:
 *    [Add to optional] — patches schema.optional_paths += path (most
 *                        common drift action — new field showed up).
 *    [Promote to core] — patches schema.core_paths += path (operator
 *                        decided this field is now required).
 *    [Ignore for now]  — client-side dismiss; schema unchanged.
 *
 *  Vanishing paths get only "Ignore" — fix should be either schema
 *  re-design (drop the core path) or operator investigation upstream.
 */
function SchemaDriftModal({
  report,
  onApply,
  onIgnore,
  onClose,
  ignoredPaths,
}: {
  report: DriftReport;
  onApply: (path: string, target: 'core' | 'optional') => Promise<void> | void;
  onIgnore: (path: string) => void;
  onClose: () => void;
  ignoredPaths: Set<string>;
}) {
  const visibleNew = report.new_paths.filter(p => !ignoredPaths.has(p.path));
  return (
    <div
      role="dialog"
      aria-label="Schema drift"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'oklch(0 0 0 / 0.55)',
        zIndex: 100, display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', padding: '40px 20px', overflow: 'auto',
      }}
    >
      <div style={{
        background: 'var(--bg)', border: '1px solid var(--line)',
        borderRadius: 10, maxWidth: 760, width: '100%', padding: 18,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            🆕 Schema drift · {report.schema_name}
          </span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
            {report.matching_events}/{report.total_events} matching · {report.near_miss_events} near-miss
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn sm ghost" onClick={onClose}>close</button>
        </div>

        {visibleNew.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)' }}>
              New fields ({visibleNew.length}) — paths seen in matching events but NOT in schema canonical.
            </div>
            {visibleNew.map(p => (
              <div key={p.path} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', border: '1px solid var(--line)',
                borderRadius: 6, fontSize: 11.5,
              }}>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink)' }}>{p.path}</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink-3)', fontSize: 10.5 }}>
                  in {p.event_count} event{p.event_count === 1 ? '' : 's'}
                </span>
                <span style={{ flex: 1 }} />
                <button
                  className="btn sm ghost"
                  onClick={() => void onApply(p.path, 'optional')}
                  title="Add to schema.optional_paths. Field will be tolerated but not required."
                  style={{ fontSize: 10.5, padding: '2px 8px' }}
                >+ optional</button>
                <button
                  className="btn sm ghost"
                  onClick={() => void onApply(p.path, 'core')}
                  title="Add to schema.core_paths. Field will be REQUIRED for match."
                  style={{ fontSize: 10.5, padding: '2px 8px', color: 'oklch(0.45 0.16 150)' }}
                >★ core</button>
                <button
                  className="btn sm ghost"
                  onClick={() => onIgnore(p.path)}
                  title="Client-side dismiss. Schema unchanged."
                  style={{ fontSize: 10.5, padding: '2px 8px', color: 'var(--ink-3)' }}
                >ignore</button>
              </div>
            ))}
          </div>
        )}

        {report.vanishing_paths.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)' }}>
              Vanishing core paths ({report.vanishing_paths.length}) — core fields missing from some matching events.
            </div>
            {report.vanishing_paths.slice(0, 20).map(p => (
              <div key={p.path} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', border: '1px solid var(--line)',
                borderRadius: 6, fontSize: 11.5, background: 'oklch(0.98 0.02 30 / 0.4)',
              }}>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink)' }}>{p.path}</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'oklch(0.55 0.10 30)', fontSize: 10.5 }}>
                  missing in {p.missing_in} event{p.missing_in === 1 ? '' : 's'}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
              Investigate upstream — this could be a lifecycle change (e.g., field set on create only) or a regression.
            </div>
          </div>
        )}

        {visibleNew.length === 0 && report.vanishing_paths.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>
            No drift to review. Schema is stable across the last {report.total_events} events.
          </div>
        )}
      </div>
    </div>
  );
}

/** T2.1 LLM merge oracle modal. Shows each proposal as a card:
 *  suggested name + confidence chip + rationale + the member groups
 *  side-by-side (chip with color + AI name + event count + path count
 *  diff). Operator clicks "Merge" → saved Schema covering all members,
 *  or "Dismiss" → drops this proposal.
 *
 *  Conservative defaults: high-confidence proposals are pre-expanded;
 *  medium/low are collapsed. Operator must consciously confirm — no
 *  bulk-confirm. */
function MergeProposalsModal({
  proposals,
  suggestions,
  aiNames,
  onConfirm,
  onDismiss,
  onClose,
}: {
  proposals: MergeProposal[];
  suggestions: ViewSuggestion[];
  aiNames: Record<string, NamedSchemaGroup>;
  onConfirm: (p: MergeProposal) => Promise<void> | void;
  onDismiss: (p: MergeProposal) => void;
  onClose: () => void;
}) {
  const byId = new Map(suggestions.map(s => [s.id, s]));
  return (
    <div
      role="dialog"
      aria-label="AI merge proposals"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'oklch(0 0 0 / 0.55)',
        zIndex: 100, display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', padding: '40px 20px', overflow: 'auto',
      }}
    >
      <div style={{
        background: 'var(--bg)', border: '1px solid var(--line)',
        borderRadius: 10, maxWidth: 900, width: '100%', padding: 18,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            🤖 Merge proposals
          </span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
            Gemini suggested {proposals.length} cluster{proposals.length === 1 ? '' : 's'}. Confirm to save as Schema; dismiss otherwise.
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn sm ghost" onClick={onClose}>close</button>
        </div>
        {proposals.map((p, i) => {
          const members = p.group_ids.map(id => byId.get(id)).filter((s): s is ViewSuggestion => !!s);
          const allPaths = new Set<string>();
          for (const m of members) for (const path of (m.paths ?? [])) allPaths.add(path);
          const allCore = new Set<string>(members[0]?.core_paths ?? members[0]?.paths ?? []);
          for (const m of members.slice(1)) {
            const c = new Set(m.core_paths ?? m.paths ?? []);
            for (const p2 of allCore) if (!c.has(p2)) allCore.delete(p2);
          }
          const totalEv = members.reduce((n, m) => n + m.estimated_count, 0);
          const confColor = p.confidence === 'high' ? 'oklch(0.45 0.16 150)'
            : p.confidence === 'medium' ? 'oklch(0.55 0.14 80)'
            : 'oklch(0.55 0.10 30)';
          const confBg = p.confidence === 'high' ? 'oklch(0.96 0.05 150 / 0.7)'
            : p.confidence === 'medium' ? 'oklch(0.96 0.06 80 / 0.7)'
            : 'oklch(0.96 0.05 30 / 0.7)';
          return (
            <div key={i} style={{
              padding: 12, border: '1px solid var(--line)', borderRadius: 8,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{p.suggested_name}</span>
                <span title={`Confidence: ${p.confidence}`} style={{
                  fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase',
                  color: confColor, background: confBg, padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                }}>{p.confidence}</span>
                <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                  {members.length} groups · {totalEv} events · {allCore.size} core / {allPaths.size} total paths
                </span>
                <span style={{ flex: 1 }} />
                <button className="btn sm ghost" onClick={() => onDismiss(p)} style={{ fontSize: 11 }}>
                  dismiss
                </button>
                <button
                  className="btn sm"
                  onClick={() => void onConfirm(p)}
                  style={{ fontSize: 11, background: 'oklch(0.96 0.05 150 / 0.7)', borderColor: 'oklch(0.85 0.12 150)', color: 'oklch(0.45 0.16 150)' }}
                >
                  ★ merge
                </button>
              </div>
              {p.rationale && (
                <div style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>{p.rationale}</div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {members.map(m => {
                  const color = colorForId(m.id);
                  const name = aiNames[m.id]?.name ?? m.matched_schema_name ?? m.name ?? 'Schema';
                  return (
                    <span key={m.id} title={`${(m.paths ?? []).length} paths · ${m.estimated_count} events`} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '3px 8px', fontSize: 11,
                      background: color.bg, border: `1px solid ${color.fg}33`,
                      borderRadius: 4, fontFamily: 'var(--mono)',
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color.fg }} />
                      {name}
                      <span style={{ color: 'var(--ink-3)' }}>{m.estimated_count}ev</span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VerifyGroupingsModal({
  suggestions,
  aiNames,
  onClose,
}: {
  suggestions: ViewSuggestion[];
  aiNames: Record<string, NamedSchemaGroup>;
  onClose: () => void;
}): React.ReactElement {
  const [showShared, setShowShared] = useState(false);
  // Flatten variants across all groups, preserving group identity for
  // coloring + sort order.
  type FlatVariant = {
    variant_id: string;
    paths: Set<string>;
    count: number;
    group_id: string;
    group_name: string;
    group_idx: number;
    group_color: ReturnType<typeof colorForId>;
    is_first_in_group: boolean;
  };
  const flat: FlatVariant[] = [];
  // Group order: by suggestion count desc (matches the visible card order).
  const orderedGroups = [...suggestions].sort((a, b) => b.estimated_count - a.estimated_count);
  orderedGroups.forEach((s, gi) => {
    const variants = s.variants && s.variants.length > 0
      ? s.variants
      : [{ fingerprint_id: s.id, paths: s.paths ?? [], count: s.estimated_count }];
    const groupName = aiNames[s.id]?.name ?? s.matched_schema_name ?? s.name;
    const color = colorForId(s.id);
    variants.forEach((v, vi) => {
      flat.push({
        variant_id: v.fingerprint_id,
        paths: new Set(v.paths),
        count: v.count,
        group_id: s.id,
        group_name: groupName,
        group_idx: gi,
        group_color: color,
        is_first_in_group: vi === 0,
      });
    });
  });

  // Union of all paths.
  const allSet = new Set<string>();
  for (const v of flat) for (const p of v.paths) allSet.add(p);
  const all = [...allSet].sort();

  // Per-path: which variants have it.
  const havingCount: Record<string, number> = {};
  for (const p of all) {
    havingCount[p] = flat.reduce((n, v) => n + (v.paths.has(p) ? 1 : 0), 0);
  }
  const distinguishing = all.filter(p => havingCount[p]! < flat.length);
  const shared = all.filter(p => havingCount[p] === flat.length);
  const visible = showShared ? all : distinguishing;

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  const groupCount = orderedGroups.length;
  const variantCount = flat.length;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'oklch(0.18 0.01 250 / 0.55)',
        display: 'flex', alignItems: 'stretch', justifyContent: 'stretch',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          margin: 24,
          flex: 1,
          background: 'var(--bg-elev)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Verify groupings</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
              {variantCount} variants across {groupCount} group{groupCount === 1 ? '' : 's'} ·
              {' '}{distinguishing.length} distinguishing field{distinguishing.length === 1 ? '' : 's'} ·
              {' '}{shared.length} shared
            </div>
          </div>
          <button
            className="btn sm ghost"
            onClick={() => setShowShared(s => !s)}
          >{showShared ? 'hide shared rows' : 'show all rows'}</button>
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
            style={{
              width: 30, height: 30, padding: 0,
              border: '1px solid var(--line)', background: 'transparent',
              borderRadius: 6, cursor: 'pointer',
              fontFamily: 'var(--mono)', fontSize: 16, color: 'var(--ink-3)',
            }}
          >×</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '12px 22px' }}>
          {/* Group header strip — shows which color band each group occupies. */}
          <div style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-elev)', paddingBottom: 6, marginBottom: 6, borderBottom: '1px dashed var(--line)' }}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
              {orderedGroups.map(s => {
                const c = colorForId(s.id);
                const variantsN = (s.variants?.length ?? 1);
                const groupName = aiNames[s.id]?.name ?? s.matched_schema_name ?? s.name;
                return (
                  <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', background: c.bg, color: c.fg, borderRadius: 3, border: `1px solid ${c.border}` }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.fg }} />
                    {groupName} · {variantsN}v · {s.estimated_count}ev
                  </span>
                );
              })}
            </div>
          </div>

          {visible.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
              All variants share the same set of fields. Differences are only in field VALUES — open per-card "compare member events" for value-level inspection.
            </div>
          ) : (
            <table style={{ borderCollapse: 'collapse', fontSize: 10.5 }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, top: 32, background: 'var(--bg-elev)', textAlign: 'left', padding: '4px 10px 4px 0', fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap', minWidth: 200, zIndex: 2 }}>
                    field
                  </th>
                  {flat.map(v => (
                    <th key={v.variant_id} title={`Variant ${v.variant_id} · group ${v.group_name} · ${v.count} event${v.count === 1 ? '' : 's'}`}
                        style={{
                          padding: '4px 4px',
                          minWidth: 22,
                          background: v.is_first_in_group ? v.group_color.bg : undefined,
                          borderLeft: v.is_first_in_group ? `2px solid ${v.group_color.fg}` : undefined,
                          textAlign: 'center', fontWeight: 500, color: 'var(--ink-3)',
                          verticalAlign: 'bottom',
                        }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, fontSize: 9 }}>
                        <span style={{ color: v.group_color.fg }}>{v.is_first_in_group ? '◀' : '·'}</span>
                        <span style={{ color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{v.count}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(p => {
                  const isShared = havingCount[p] === flat.length;
                  return (
                    <tr key={p}>
                      <td style={{
                        position: 'sticky', left: 0, background: 'var(--bg-elev)', zIndex: 1,
                        padding: '1px 10px 1px 0',
                        fontFamily: 'var(--mono)', fontSize: 10.5,
                        color: isShared ? 'var(--ink-3)' : 'var(--ink-2)',
                        whiteSpace: 'nowrap',
                        fontWeight: isShared ? 400 : 500,
                        borderBottom: '1px solid oklch(0.92 0 0 / 0.4)',
                      }}>{p}</td>
                      {flat.map(v => {
                        const has = v.paths.has(p);
                        return (
                          <td key={v.variant_id} style={{
                            padding: '1px 4px',
                            textAlign: 'center',
                            fontFamily: 'var(--mono)', fontSize: 11,
                            background: has ? v.group_color.bg : undefined,
                            color: has ? v.group_color.fg : 'var(--ink-3)',
                            opacity: has ? 1 : 0.25,
                            fontWeight: !isShared && has ? 600 : 400,
                            borderLeft: v.is_first_in_group ? `2px solid ${v.group_color.fg}` : undefined,
                            borderBottom: '1px solid oklch(0.92 0 0 / 0.4)',
                          }}>{has ? '✓' : '·'}</td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{
          padding: '10px 22px',
          borderTop: '1px solid var(--line)',
          fontSize: 11, color: 'var(--ink-3)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span><strong style={{ color: 'var(--ink-2)' }}>How to read:</strong> each column = one fingerprint variant. Variants from the same group share a colored background + sit adjacent (▶ marks group boundary). A "clean" grouping shows roughly-uniform ✓ patterns within each color block. An <strong>outlier column</strong> inside a colored block (very different ✓ pattern from its neighbours) hints "maybe this variant shouldn't be in this group".</span>
        </div>
      </div>
    </div>
  );
}

/** Cross-group diff: operator picks 2+ groups via checkbox; we show
 *  a path × group matrix highlighting fields unique to each group.
 *  Each cell shows the path's %-presence WITHIN that group — solid
 *  ✓ when core (100%), faded ✓ + percent when optional, · when
 *  missing entirely. Distinguishing fields (present in some groups
 *  but not all) are highlighted; default view hides shared rows. */
function CrossGroupDiffMatrix({
  groups,
}: {
  groups: Array<{
    id: string;
    name: string;
    count: number;
    core: string[];
    optional: string[];
    presence: Record<string, number>;
  }>;
}): React.ReactElement {
  const [showShared, setShowShared] = useState(false);
  // Union all paths across selected groups.
  const allSet = new Set<string>();
  for (const g of groups) {
    for (const p of g.core) allSet.add(p);
    for (const p of g.optional) allSet.add(p);
  }
  const all = [...allSet].sort();
  // Membership map: per path, which groups have it AT ALL (presence > 0).
  const presenceByGroup: Array<Set<string>> = groups.map(g => new Set([...g.core, ...g.optional]));
  const havingCount: Record<string, number> = {};
  for (const p of all) {
    havingCount[p] = presenceByGroup.reduce((n, set) => n + (set.has(p) ? 1 : 0), 0);
  }
  const distinguishing = all.filter(p => havingCount[p]! < groups.length);
  const shared = all.filter(p => havingCount[p] === groups.length);
  const visible = showShared ? all : distinguishing;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8, fontSize: 11 }}>
        <span style={{ color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
          {distinguishing.length} distinguishing · {shared.length} shared · {all.length} total
        </span>
        <button
          className="btn sm ghost"
          onClick={() => setShowShared(s => !s)}
          style={{ fontSize: 10, padding: '2px 8px' }}
        >{showShared ? 'hide shared' : 'show all'}</button>
      </div>
      {visible.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          All selected groups have the exact same fields. They differ only in field VALUES, not field presence — try the per-group "compare member events" view to inspect those.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 10.5 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 10px 4px 0', fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                  field
                </th>
                {groups.map((g, i) => {
                  const c = colorForId(g.id);
                  return (
                    <th key={g.id} style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 500, color: 'var(--ink-2)', minWidth: 90, verticalAlign: 'bottom' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.fg }} />
                        <span style={{ fontSize: 11, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.name}>{g.name}</span>
                        <span style={{ fontSize: 9, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{g.count} ev</span>
                      </div>
                      {/* hidden marker so column index is preserved when no rows render */}
                      <span aria-hidden="true" style={{ display: 'none' }}>{i}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map(p => {
                const isShared = havingCount[p] === groups.length;
                return (
                  <tr key={p}>
                    <td style={{
                      padding: '2px 10px 2px 0',
                      fontFamily: 'var(--mono)', fontSize: 10.5,
                      color: isShared ? 'var(--ink-3)' : 'var(--ink-2)',
                      whiteSpace: 'nowrap',
                      fontWeight: isShared ? 400 : 500,
                    }}>{p}</td>
                    {groups.map((g, i) => {
                      const has = presenceByGroup[i]!.has(p);
                      const pres = g.presence[p] ?? 0;
                      const pct = g.count > 0 ? Math.round((pres / g.count) * 100) : 0;
                      const isCore = has && pct === 100;
                      return (
                        <td key={g.id} style={{
                          padding: '2px 8px',
                          textAlign: 'center',
                          fontFamily: 'var(--mono)', fontSize: 11,
                          background: !isShared && has ? 'oklch(0.95 0.06 145 / 0.45)' : undefined,
                          color: has
                            ? (isCore ? 'oklch(0.45 0.16 145)' : 'var(--accent)')
                            : 'var(--ink-3)',
                          opacity: has ? 1 : 0.3,
                          fontWeight: !isShared && has ? 600 : 400,
                        }}>
                          {has ? (
                            isCore
                              ? '✓'
                              : <span title={`${pres}/${g.count} = ${pct}%`}>✓<span style={{ fontSize: 9, marginLeft: 2, opacity: 0.7 }}>{pct}%</span></span>
                          ) : '·'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Variant-level diff: when Jaccard merged multiple fingerprint
 *  variants into one group, show their actual differences. Default
 *  view collapses paths shared by ALL variants (the "boring" common
 *  ones) and surfaces the distinguishing fields prominently — the
 *  operator's question is "what's actually different?". Toggle to
 *  show all paths. */
function VariantDiffMatrix({
  variants,
}: {
  variants: Array<{ fingerprint_id: string; paths: string[]; count: number }>;
}): React.ReactElement {
  const [showShared, setShowShared] = useState(false);
  // Compute union + intersection across all variants.
  const allSet = new Set<string>();
  for (const v of variants) for (const p of v.paths) allSet.add(p);
  const all = [...allSet].sort();
  const variantSets = variants.map(v => new Set(v.paths));
  // For each path, count how many variants have it.
  const presence: Record<string, number> = {};
  for (const p of all) {
    let c = 0;
    for (const s of variantSets) if (s.has(p)) c++;
    presence[p] = c;
  }
  const distinguishing = all.filter(p => presence[p]! < variants.length);
  const shared = all.filter(p => presence[p] === variants.length);
  const visible = showShared ? all : distinguishing;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {variants.length} variants · {distinguishing.length} distinguishing field{distinguishing.length === 1 ? '' : 's'} · {shared.length} shared
        </span>
        <button
          className="btn sm ghost"
          onClick={() => setShowShared(s => !s)}
          style={{ fontSize: 10, padding: '2px 8px' }}
        >{showShared ? 'hide shared' : 'show all'}</button>
      </div>
      {visible.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          All variants share the exact same fields. Differences are only in field VALUES, not presence.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 10.5 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '2px 8px 4px 0', fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                  field
                </th>
                {variants.map((v, i) => (
                  <th key={v.fingerprint_id} title={`Variant ${v.fingerprint_id} · ${v.count} event${v.count === 1 ? '' : 's'}`}
                      style={{ padding: '2px 6px 4px', textAlign: 'center', fontWeight: 500, color: 'var(--ink-3)', whiteSpace: 'nowrap', minWidth: 50 }}>
                    <div style={{ fontFamily: 'var(--mono)' }}>v{i + 1}</div>
                    <div style={{ fontSize: 9, opacity: 0.7 }}>{v.count} ev</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(p => {
                const pres = presence[p]!;
                const isShared = pres === variants.length;
                return (
                  <tr key={p}>
                    <td style={{
                      padding: '1px 8px 1px 0',
                      fontFamily: 'var(--mono)', fontSize: 10.5,
                      color: isShared ? 'var(--ink-3)' : 'var(--ink-2)',
                      whiteSpace: 'nowrap',
                      fontWeight: isShared ? 400 : 500,
                    }}>{p}</td>
                    {variantSets.map((set, i) => {
                      const has = set.has(p);
                      // Distinguishing path: highlight the variant(s) that have it.
                      return (
                        <td key={i} style={{
                          padding: '1px 6px',
                          textAlign: 'center',
                          fontFamily: 'var(--mono)', fontSize: 11,
                          background: !isShared && has ? 'oklch(0.95 0.06 145 / 0.5)' : undefined,
                          color: has
                            ? (isShared ? 'var(--ink-3)' : 'oklch(0.45 0.16 145)')
                            : 'var(--ink-3)',
                          opacity: has ? 1 : 0.3,
                          fontWeight: !isShared && has ? 600 : 400,
                        }}>{has ? '✓' : '·'}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Group-level diff: shows which paths are present (✓) vs missing (·)
 *  per member event. Paths sorted by presence desc so all-members-have
 *  rows sit at top, optional with most spread sit further down — easy
 *  to scan how members differ. Truncated to first 40 paths to keep
 *  the matrix on-screen; "+N more" hint when overflow. */
function MemberDiffMatrix({
  samples,
  paths,
  presence,
  count,
}: {
  samples: Array<{ event_id: string; received_at: string; paths: string[] }>;
  paths: string[];
  presence: Record<string, number>;
  count: number;
}): React.ReactElement {
  // Sort paths by presence desc, then alphabetic.
  const sorted = [...paths].sort((a, b) => {
    const pa = presence[a] ?? 0, pb = presence[b] ?? 0;
    if (pa !== pb) return pb - pa;
    return a.localeCompare(b);
  });
  const visible = sorted.slice(0, 40);
  const overflow = sorted.length - visible.length;
  // Each sample's path set for fast lookup.
  const sampleSets = samples.map(s => new Set(s.paths));
  return (
    <div style={{ marginTop: 8, overflowX: 'auto', maxWidth: '100%' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 10.5 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '2px 8px 4px 0', fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
              field
            </th>
            <th style={{ padding: '2px 6px 4px', textAlign: 'center', fontWeight: 500, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
              %
            </th>
            {samples.map((s, i) => (
              <th key={s.event_id} title={`${s.event_id} · ${new Date(s.received_at).toLocaleString()}`}
                  style={{ padding: '2px 4px 4px', textAlign: 'center', fontWeight: 500, color: 'var(--ink-3)', minWidth: 18 }}>
                #{i + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map(p => {
            const pres = presence[p] ?? 0;
            const pct = count > 0 ? Math.round((pres / count) * 100) : 0;
            const isCore = pres === count;
            return (
              <tr key={p}>
                <td style={{
                  padding: '1px 8px 1px 0',
                  fontFamily: 'var(--mono)', fontSize: 10.5,
                  color: isCore ? 'var(--ink-2)' : 'var(--ink-3)',
                  whiteSpace: 'nowrap',
                }}>{p}</td>
                <td style={{
                  padding: '1px 6px',
                  textAlign: 'center',
                  fontFamily: 'var(--mono)', fontSize: 10,
                  color: isCore ? 'oklch(0.55 0.14 145)' : 'var(--ink-3)',
                  fontWeight: isCore ? 600 : 400,
                }}>{pct}%</td>
                {sampleSets.map((set, i) => {
                  const has = set.has(p);
                  return (
                    <td key={i} style={{
                      padding: '1px 4px',
                      textAlign: 'center',
                      color: has ? (isCore ? 'oklch(0.55 0.14 145)' : 'var(--accent)') : 'var(--ink-3)',
                      opacity: has ? 1 : 0.35,
                      fontFamily: 'var(--mono)', fontSize: 11,
                    }}>{has ? '✓' : '·'}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {overflow > 0 && (
        <div style={{ marginTop: 4, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-3)' }}>
          +{overflow} more field{overflow === 1 ? '' : 's'} not shown
        </div>
      )}
    </div>
  );
}

export function FingerprintGroupsV2({ source }: Props): React.ReactElement {
  const toast = useToast();
  const [suggestions, setSuggestions] = useState<ViewSuggestion[]>([]);
  const [sampled, setSampled] = useState(0);
  const [loading, setLoading] = useState(true);
  const [aiNames, setAiNames] = useState<Record<string, NamedSchemaGroup>>({});
  const [naming, setNaming] = useState(false);
  // T2.1 LLM merge oracle. Operator clicks "🤖 propose merges", we
  // call /views:propose-merges, then show proposals in a modal where
  // each can be confirmed (creates a saved Schema covering the merged
  // fingerprints) or dismissed.
  const [proposingMerges, setProposingMerges] = useState(false);
  const [mergeProposals, setMergeProposals] = useState<MergeProposal[] | null>(null);
  // T3.1 + 3.2 schema-evolution drift. Loaded silently on mount so
  // saved-schema chips can show a "🆕 N drift" badge without an
  // operator click. Per-schema modal lists the drifted paths with
  // [Add to optional] / [Promote to core] / [Ignore] actions.
  const [driftReports, setDriftReports] = useState<Record<string, DriftReport>>({});
  const [driftModalSchemaId, setDriftModalSchemaId] = useState<string | null>(null);
  const [ignoredDriftPaths, setIgnoredDriftPaths] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getViewSuggestions(source.source_id)
      .then(r => {
        if (!alive) return;
        setSuggestions(r.suggestions ?? []);
        setSampled(r.sampled ?? 0);
        // Server-side AI name cache (Firestore, 7d TTL) — shared across
        // every device. Seeds local state so cards render with names
        // immediately even on a fresh browser. Backed by per-suggestion
        // ai_cached_* fields populated by the auto-suggest endpoint.
        const fromServer: Record<string, NamedSchemaGroup> = {};
        for (const s of r.suggestions ?? []) {
          if (s.ai_cached_name) {
            fromServer[s.id] = {
              id: s.id,
              name: s.ai_cached_name,
              description: s.ai_cached_description,
              tags: s.ai_cached_tags,
            };
          }
        }
        setAiNames(prev => ({ ...prev, ...fromServer }));
        setLoading(false);
      })
      .catch(() => { if (alive) setLoading(false); });
    // Drift feed runs in parallel — no spinner, just populates the
    // badges as data arrives. Failure here doesn't block the UI.
    getSchemaDriftFeed(source.source_id)
      .then(reports => {
        if (!alive) return;
        const map: Record<string, DriftReport> = {};
        for (const r of reports) map[r.schema_id] = r;
        setDriftReports(map);
      })
      .catch(() => { /* silent — drift is supplemental */ });
    return () => { alive = false; };
  }, [source.source_id]);

  // Restore AI names from localStorage as a fast-path optimisation.
  // Server-side cache (read above from r.suggestions[].ai_cached_*)
  // is the source of truth for cross-device persistence; localStorage
  // is just to render the UI without waiting for the suggestions
  // round-trip on a quick reload.
  // Cross-group compare — operator picks 2+ groups via checkbox; we
  // render a path × group matrix highlighting fields unique to each
  // selection. Useful for "are these two groups actually different?".
  const [comparing, setComparing] = useState<Set<string>>(new Set());

  // Full-screen "verify groupings" matrix. Shows EVERY fingerprint
  // variant across ALL groups in a single matrix so the operator can
  // scan visually + confirm the grouping algorithm did the right
  // thing. Same-group columns sit together and share the group's
  // color; different-group columns look visually distinct.
  const [showVerify, setShowVerify] = useState(false);

  // Focus mode: ONE group is active (primary), others can be added
  // for comparison either WHOLE (all variants) or by individual
  // variant. Default null → classic grid view; setting it switches
  // to focus + compare layout.
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);
  // Group ids whose ALL variants are included in compare.
  const [compareGroupIds, setCompareGroupIds] = useState<Set<string>>(new Set());
  // Specific variant ids from any group, format `${groupId}::${variantId}`.
  const [compareVariantIds, setCompareVariantIds] = useState<Set<string>>(new Set());

  function exitFocus() {
    setFocusedGroupId(null);
    setCompareGroupIds(new Set());
    setCompareVariantIds(new Set());
  }
  function toggleCompareGroup(gid: string) {
    setCompareGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  }
  function toggleCompareVariant(gid: string, vid: string) {
    const key = `${gid}::${vid}`;
    setCompareVariantIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const aiCacheKey = `dh:v2:fp_ai_names:${source.source_id}`;
  useEffect(() => {
    try {
      const raw = localStorage.getItem(aiCacheKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, NamedSchemaGroup>;
        if (parsed && typeof parsed === 'object') setAiNames(parsed);
      }
    } catch { /* private mode etc. */ }
  }, [aiCacheKey]);

  async function runAutoName() {
    if (naming) return;
    if (suggestions.length === 0) {
      toast.show('No groups to name yet — wait for events.', { kind: 'info' });
      return;
    }
    setNaming(true);
    try {
      const named = await autoNameViewGroups(source.source_id);
      const map: Record<string, NamedSchemaGroup> = {};
      for (const n of named) map[n.id] = n;
      setAiNames(map);
      try { localStorage.setItem(aiCacheKey, JSON.stringify(map)); } catch { /* ignore */ }
      toast.show(`Named ${named.length} group${named.length === 1 ? '' : 's'} with AI.`, { kind: 'success' });
    } catch (e) {
      toast.show(`Auto-name failed: ${(e as Error).message.slice(0, 120)}`, { kind: 'error' });
    } finally {
      setNaming(false);
    }
  }

  function clearAINames() {
    setAiNames({});
    try { localStorage.removeItem(aiCacheKey); } catch { /* ignore */ }
  }

  async function runProposeMerges() {
    if (proposingMerges) return;
    if (suggestions.length < 2) {
      toast.show('Need at least 2 groups before proposing merges.', { kind: 'info' });
      return;
    }
    setProposingMerges(true);
    try {
      const r = await proposeSchemaMerges(source.source_id);
      if (r.llm_error) {
        toast.show(`AI failed: ${r.llm_error.slice(0, 120)}`, { kind: 'error' });
        return;
      }
      if (r.proposals.length === 0) {
        toast.show('AI found no merge candidates — current grouping looks clean.', { kind: 'info' });
        setMergeProposals([]);
        return;
      }
      setMergeProposals(r.proposals);
    } catch (e) {
      toast.show(`Propose merges failed: ${(e as Error).message.slice(0, 120)}`, { kind: 'error' });
    } finally {
      setProposingMerges(false);
    }
  }

  async function applyDriftPathToSchema(
    schemaId: string,
    path: string,
    target: 'core' | 'optional',
  ) {
    try {
      const cur = await import('../../api').then(m => m.listSchemaDefs(source.source_id));
      const schema = cur.find(s => s.schema_id === schemaId);
      if (!schema) {
        toast.show('Schema not found.', { kind: 'error' });
        return;
      }
      const newCore = new Set(schema.core_paths ?? []);
      const newOptional = new Set(schema.optional_paths ?? []);
      if (target === 'core') {
        newCore.add(path);
        newOptional.delete(path);
      } else {
        if (!newCore.has(path)) newOptional.add(path);
      }
      await patchSchemaDef(source.source_id, schemaId, {
        core_paths: [...newCore].sort(),
        optional_paths: [...newOptional].sort(),
      });
      toast.show(`Added ${path} → ${target}.`, { kind: 'success' });
      // Re-fetch drift to drop the path from new_paths.
      const updated = await getSchemaDriftFeed(source.source_id);
      const map: Record<string, DriftReport> = {};
      for (const r of updated) map[r.schema_id] = r;
      setDriftReports(map);
    } catch (e) {
      toast.show(`Apply failed: ${(e as Error).message.slice(0, 120)}`, { kind: 'error' });
    }
  }

  function ignoreDriftPath(path: string) {
    setIgnoredDriftPaths(prev => new Set([...prev, path]));
  }

  async function confirmMergeProposal(proposal: MergeProposal) {
    try {
      // Persist as a Schema Definition covering all merged fingerprints.
      // T1.4 already supports multi-fingerprint schemas via
      // source_fingerprints[]. Take union of paths from member groups
      // as core_paths; intersection across all members would be safer
      // but the T1.1 merge already did Jaccard ≥ 0.80 so union is fine.
      const members = suggestions.filter(s => proposal.group_ids.includes(s.id));
      if (members.length < 2) {
        toast.show('Some groups disappeared since proposals — re-run.', { kind: 'error' });
        return;
      }
      const allPaths = new Set<string>();
      const corePaths = new Set<string>(members[0]!.core_paths ?? members[0]!.paths ?? []);
      for (const m of members) {
        for (const p of (m.paths ?? [])) allPaths.add(p);
        // Intersection of cores across members — safe AND-of-is_present.
        const memberCore = new Set(m.core_paths ?? m.paths ?? []);
        for (const p of corePaths) if (!memberCore.has(p)) corePaths.delete(p);
      }
      // Tags: take the most-confident first member's tags as base.
      const baseTags = aiNames[members[0]!.id]?.tags ?? {};
      const totalEvents = members.reduce((n, m) => n + m.estimated_count, 0);
      await saveSchemaDef(source.source_id, {
        name: proposal.suggested_name,
        tags: baseTags,
        core_paths: [...corePaths].sort(),
        optional_paths: [...allPaths].filter(p => !corePaths.has(p)).sort(),
        source_fingerprints: members.map(m => m.id),
        member_event_count: totalEvents,
      });
      toast.show(`Merged ${members.length} groups → "${proposal.suggested_name}".`, { kind: 'success' });
      // Drop this proposal from the modal; refetch suggestions to
      // show the new ★saved schema status on the merged groups.
      setMergeProposals(prev => (prev ?? []).filter(p => p !== proposal));
      const r = await getViewSuggestions(source.source_id);
      setSuggestions(r.suggestions ?? []);
      setSampled(r.sampled ?? 0);
    } catch (e) {
      toast.show(`Merge save failed: ${(e as Error).message.slice(0, 120)}`, { kind: 'error' });
    }
  }

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>Loading schema groups…</div>;
  }
  if (suggestions.length === 0) {
    return <EmptyState icon="◈" title="No schema groups yet" body="Schema groups appear once events arrive — distinct payload shapes get bucketed by canonical fingerprint." />;
  }

  // Tags that are CONSTANT across every group (e.g. `source=X` when
  // we're inside source X) carry no per-group signal — they just add
  // visual noise to every card. Lift them once to a context strip at
  // the top, hide from per-group chips. When the same component runs
  // at workspace level later (cross-source view), source-specific tags
  // would no longer be constant and re-appear per group automatically.
  const allTagSets = suggestions
    .map(s => aiNames[s.id]?.tags ?? {})
    .filter(t => Object.keys(t).length > 0);
  const constantTags: Record<string, string> = {};
  if (allTagSets.length > 0) {
    const firstKeys = Object.keys(allTagSets[0]!);
    for (const k of firstKeys) {
      const v = allTagSets[0]![k]!;
      if (allTagSets.every(t => t[k] === v)) constantTags[k] = v;
    }
  }
  const constantKeys = new Set(Object.keys(constantTags));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
          {suggestions.length} group{suggestions.length === 1 ? '' : 's'} · sampled {sampled}
        </span>
        <span style={{ flex: 1 }} />
        {Object.keys(aiNames).length > 0 && (
          <button className="btn sm ghost" onClick={clearAINames} title="Drop AI-generated names; revert to Schema·N defaults">clear AI names</button>
        )}
        <button
          className="btn sm ghost"
          onClick={() => setShowVerify(true)}
          disabled={suggestions.length === 0}
          title="Open the full grouping matrix in fullscreen. Every fingerprint variant across every group, side by side. Quick visual scan to confirm the auto-grouping is correct."
        >
          🔍 verify groupings
        </button>
        <button
          className="btn sm"
          onClick={() => void runAutoName()}
          disabled={naming || suggestions.length === 0}
          title="Use Gemini to assign meaningful names based on source context, sample data, and field structure."
          style={{ background: 'var(--accent-soft)', borderColor: 'var(--accent-line)', color: 'var(--accent)' }}
        >
          {naming ? 'naming…' : '✨ name with AI'}
        </button>
        <button
          className="btn sm"
          onClick={() => void runProposeMerges()}
          disabled={proposingMerges || suggestions.length < 2}
          title="Ask Gemini which groups represent the SAME logical entity (just shape variations) and should be merged into a single Schema. AI on-request only."
          style={{ background: 'oklch(0.96 0.06 290 / 0.7)', borderColor: 'oklch(0.85 0.12 290)', color: 'oklch(0.45 0.18 290)' }}
        >
          {proposingMerges ? 'thinking…' : '🤖 propose merges'}
        </button>
      </div>

      {mergeProposals !== null && mergeProposals.length > 0 && (
        <MergeProposalsModal
          proposals={mergeProposals}
          suggestions={suggestions}
          aiNames={aiNames}
          onConfirm={confirmMergeProposal}
          onDismiss={(p) => setMergeProposals(prev => (prev ?? []).filter(x => x !== p))}
          onClose={() => setMergeProposals(null)}
        />
      )}

      {driftModalSchemaId && driftReports[driftModalSchemaId] && (
        <SchemaDriftModal
          report={driftReports[driftModalSchemaId]!}
          onApply={async (path, target) => {
            await applyDriftPathToSchema(driftModalSchemaId, path, target);
          }}
          onIgnore={ignoreDriftPath}
          onClose={() => setDriftModalSchemaId(null)}
          ignoredPaths={ignoredDriftPaths}
        />
      )}

      {showVerify && (
        <VerifyGroupingsModal
          suggestions={suggestions}
          aiNames={aiNames}
          onClose={() => setShowVerify(false)}
        />
      )}

      {comparing.size >= 2 && (() => {
        const picked = suggestions.filter(s => comparing.has(s.id));
        return (
          <div style={{
            padding: 14,
            background: 'var(--bg-elev)',
            border: '1px solid var(--accent-line)',
            borderRadius: 8,
            marginBottom: 6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 600 }}>
                cross-group diff · {picked.length} groups
              </span>
              <span style={{ flex: 1 }} />
              <button className="btn sm ghost" onClick={() => setComparing(new Set())} style={{ fontSize: 11 }}>clear selection</button>
            </div>
            <CrossGroupDiffMatrix groups={picked.map(s => ({
              id: s.id,
              name: aiNames[s.id]?.name ?? s.matched_schema_name ?? s.name,
              count: s.estimated_count,
              core: s.core_paths ?? [],
              optional: s.optional_paths ?? [],
              presence: s.path_presence ?? {},
            }))} />
          </div>
        );
      })()}

      {Object.keys(constantTags).length > 0 && (
        <div style={{
          display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
          padding: '6px 10px', background: 'var(--bg-sunken)',
          border: '1px solid var(--line)', borderRadius: 6,
          marginBottom: 6,
        }}>
          <span title="These tags are identical across every group on this page so they live here once instead of repeating on every card."
                style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
            context
          </span>
          {Object.entries(constantTags).map(([k, v]) => (
            <TagChip key={k} k={k} v={v} />
          ))}
        </div>
      )}

      {focusedGroupId ? (
        <FocusModeView
          suggestions={suggestions}
          aiNames={aiNames}
          focusedGroupId={focusedGroupId}
          compareGroupIds={compareGroupIds}
          compareVariantIds={compareVariantIds}
          constantKeys={constantKeys}
          onSelectFocus={setFocusedGroupId}
          onToggleCompareGroup={toggleCompareGroup}
          onToggleCompareVariant={toggleCompareVariant}
          onExit={exitFocus}
        />
      ) : (
      <>
      {/* Hierarchical umbrella grouping — composite key (entity + derivation)
          so a flat-derivative and its raw parent never collapse into one
          umbrella, even when both have entity=deal. The user feedback was
          explicit: "flat and non-flat are NOT the same dataset." Each
          derivation kind (input / flatten_derivative / array_expand_element /
          field_map_canonical / derived / enriched / reingest / derivative)
          gets its own visual section. Falls back to entity-only when only
          one derivation kind is present (avoids unnecessary nesting). */}
      {(() => {
        const ENTITY_KEY = 'entity';
        const DERIV_KEY = 'derivation';
        const entityCandidate = !constantKeys.has(ENTITY_KEY);
        // Derivation only contributes to the umbrella key when it varies
        // across groups — otherwise all groups share the same value and
        // the suffix becomes noise. constantKeys already lifts perfectly
        // constant tags out, but we double-check so single-derivation
        // sources don't get a redundant "(input)" suffix.
        const derivValues = new Set<string>();
        for (const s of suggestions) {
          const v = aiNames[s.id]?.tags?.[DERIV_KEY];
          if (v) derivValues.add(v);
        }
        const derivVaries = derivValues.size > 1;
        const buckets = new Map<string, ViewSuggestion[]>();
        for (const s of suggestions) {
          const tags = aiNames[s.id]?.tags ?? {};
          const ent = entityCandidate ? (tags[ENTITY_KEY] ?? '__other__') : '__flat__';
          const der = derivVaries ? (tags[DERIV_KEY] ?? '__unknown__') : '';
          // Composite key. Display label is built from the same parts in
          // the umbrella header below.
          const k = derivVaries ? `${ent}|${der}` : ent;
          if (!buckets.has(k)) buckets.set(k, []);
          buckets.get(k)!.push(s);
        }
        const ordered = [...buckets.entries()].sort((a, b) => {
          const ac = a[1].reduce((n, s) => n + s.estimated_count, 0);
          const bc = b[1].reduce((n, s) => n + s.estimated_count, 0);
          return bc - ac;
        });
        return ordered.map(([umbrella, group]) => {
          // Decompose the composite key for display.
          const [ent, der] = umbrella.includes('|') ? umbrella.split('|') as [string, string] : [umbrella, ''];
          const entLabel = ent === '__other__' ? 'Other' : ent === '__flat__' ? '' : ent;
          const derLabel = der && der !== '__unknown__' ? der.replace(/_/g, ' ') : '';
          const showUmbrella = entityCandidate && ent !== '__flat__' &&
            (group.length > 1 || (ent !== '__other__' && group.length === 1) || derLabel !== '');
          return (
            <div key={umbrella} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {showUmbrella && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0', borderBottom: '1px dashed var(--line)' }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', textTransform: 'capitalize' }}>
                    {entLabel}
                  </span>
                  {derLabel && (
                    <span title="Derivation kind — flat-derivative, array_expand_element, field_map_canonical etc. are NOT the same dataset as the input even when entity matches."
                          style={{
                            fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.04em',
                            color: 'oklch(0.45 0.14 280)', background: 'oklch(0.96 0.05 280 / 0.6)',
                            padding: '1px 6px', borderRadius: 3, border: '1px solid oklch(0.85 0.10 280)',
                          }}>
                      {derLabel}
                    </span>
                  )}
                  <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                    {group.length} variant{group.length === 1 ? '' : 's'} · {group.reduce((n, s) => n + s.estimated_count, 0)} ev
                  </span>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
                {group.map(s => {
                  const color = colorForId(s.id);
                  const aiName = aiNames[s.id];
                  const displayName = aiName?.name ?? s.name;
                  const description = aiName?.description ?? s.blurb;
                  const corePaths = s.core_paths ?? [];
                  const optionalPaths = s.optional_paths ?? [];
                  return (
                    <div
                      key={s.id}
                      style={{
                        padding: 12,
                        background: 'var(--bg)',
                        border: `1px solid ${color.border}`,
                        borderLeft: `4px solid ${color.fg}`,
                        borderRadius: 6,
                        display: 'flex', flexDirection: 'column', gap: 6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <input
                          type="checkbox"
                          checked={comparing.has(s.id)}
                          onChange={() => setComparing(prev => {
                            const next = new Set(prev);
                            if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                            return next;
                          })}
                          title="Pick 2 or more to compare across groups"
                          style={{ flexShrink: 0, cursor: 'pointer' }}
                          aria-label="Select for comparison"
                        />
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color.fg, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', flex: 1, minWidth: 0 }}>
                          {s.matched_schema_name ?? displayName}
                        </span>
                        <button
                          className="btn sm ghost"
                          onClick={() => { setFocusedGroupId(s.id); setComparing(new Set()); }}
                          title="Focus on this group. Other groups become comparison candidates with per-group + per-variant toggles."
                          style={{ fontSize: 10.5, padding: '2px 8px' }}
                        >🎯 focus</button>
                        {s.matched_schema_id && (
                          <span title={`Saved Schema ${s.matched_schema_id} — operator-named, persists across drift`}
                                style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'oklch(0.45 0.16 150)', padding: '1px 6px', background: 'oklch(0.96 0.05 150 / 0.7)', borderRadius: 3, fontWeight: 600 }}>
                            ★ saved
                          </span>
                        )}
                        {(() => {
                          if (!s.matched_schema_id) return null;
                          const drift = driftReports[s.matched_schema_id];
                          if (!drift) return null;
                          const newCount = drift.new_paths.filter(p => !ignoredDriftPaths.has(p.path)).length;
                          const vanishCount = drift.vanishing_paths.length;
                          const total = newCount + vanishCount;
                          if (total === 0) return null;
                          return (
                            <button
                              className="btn sm ghost"
                              onClick={(e) => { e.stopPropagation(); setDriftModalSchemaId(s.matched_schema_id!); }}
                              title={`Schema-evolution drift detected: ${newCount} new path${newCount === 1 ? '' : 's'}, ${vanishCount} vanishing. Click to review.`}
                              style={{
                                fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 600,
                                color: 'oklch(0.45 0.18 60)', background: 'oklch(0.96 0.06 60 / 0.7)',
                                padding: '1px 6px', borderRadius: 3, border: '1px solid oklch(0.85 0.12 60)',
                              }}
                            >
                              🆕 {total} drift
                            </button>
                          );
                        })()}
                        {!s.matched_schema_id && aiName && (
                          <span title="Named by AI" style={{ fontSize: 10, fontFamily: 'var(--mono)', color: color.fg, padding: '1px 6px', background: color.bg, borderRadius: 3 }}>
                            ✨ AI
                          </span>
                        )}
                        <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)', padding: '1px 6px', background: 'var(--bg-sunken)', borderRadius: 3 }}>
                          {s.estimated_count} ev
                        </span>
                      </div>
                      {description && (
                        <div style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>{description}</div>
                      )}
                      {aiName?.tags && Object.entries(aiName.tags).filter(([k]) => !constantKeys.has(k) && k !== ENTITY_KEY && (!derivVaries || k !== DERIV_KEY)).length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                          {Object.entries(aiName.tags)
                            .filter(([k]) => !constantKeys.has(k) && k !== ENTITY_KEY && (!derivVaries || k !== DERIV_KEY))
                            .map(([k, v]) => <TagChip key={k} k={k} v={v} />)}
                        </div>
                      )}
                      <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                        {corePaths.length} core
                        {optionalPaths.length > 0 && <> · {optionalPaths.length} optional</>}
                        {s.origin_summary && <> · {s.origin_summary}</>}
                      </div>
                      {(corePaths.length > 0 || optionalPaths.length > 0) && (
                        <details style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                          <summary style={{ cursor: 'pointer' }}>fields</summary>
                          <div style={{ marginTop: 4, lineHeight: 1.6, wordBreak: 'break-word' }}>
                            {corePaths.length > 0 && (
                              <div style={{ marginBottom: 4 }}>
                                <span style={{ color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6, fontSize: 10 }}>core</span>
                                <span style={{ color: 'var(--ink-2)' }}>{corePaths.slice(0, 30).join(', ')}{corePaths.length > 30 ? `, +${corePaths.length - 30}` : ''}</span>
                              </div>
                            )}
                            {optionalPaths.length > 0 && (
                              <div>
                                <span style={{ color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6, fontSize: 10 }}>optional</span>
                                <span style={{ color: 'var(--ink-3)', opacity: 0.85 }}>
                                  {optionalPaths.slice(0, 30).map((p, i) => {
                                    const pres = s.path_presence?.[p];
                                    const pct = pres && s.estimated_count ? Math.round((pres / s.estimated_count) * 100) : null;
                                    return <span key={p}>{i > 0 && ', '}{p}{pct !== null && <span style={{ opacity: 0.6 }}> {pct}%</span>}</span>;
                                  })}
                                  {optionalPaths.length > 30 && <>, +{optionalPaths.length - 30}</>}
                                </span>
                              </div>
                            )}
                          </div>
                        </details>
                      )}
                      {s.variants && s.variants.length > 1 && (
                        <details style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                          <summary title="The Jaccard merge collapsed these distinct shape variants into one group. Diff shows which fields each variant has." style={{ cursor: 'pointer' }}>
                            compare variants ({s.variants.length})
                          </summary>
                          <VariantDiffMatrix variants={s.variants} />
                        </details>
                      )}
                      {s.member_samples && s.member_samples.length > 1 && (corePaths.length + optionalPaths.length) > 0 && (
                        <details style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                          <summary style={{ cursor: 'pointer' }}>compare member events ({s.member_samples.length})</summary>
                          <MemberDiffMatrix
                            samples={s.member_samples}
                            paths={[...corePaths, ...optionalPaths]}
                            presence={s.path_presence ?? {}}
                            count={s.estimated_count}
                          />
                        </details>
                      )}
                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                        {!s.matched_schema_id ? (
                          <button
                            className="btn sm ghost"
                            title="Save this group as a Schema Definition. Future events that match (≥80% Jaccard on core paths) will attach to this name + tags automatically — survives schema drift."
                            onClick={async () => {
                              try {
                                const tags: Record<string, string> = { ...(aiName?.tags ?? {}) };
                                // Ensure constant tags get baked into saved tags so cross-source views still see them
                                Object.assign(tags, constantTags);
                                await saveSchemaDef(source.source_id, {
                                  name: aiName?.name ?? `Schema · ${(s.paths?.length ?? 0)} fields`,
                                  description: aiName?.description,
                                  tags,
                                  core_paths: s.core_paths ?? [],
                                  optional_paths: s.optional_paths ?? [],
                                  source_fingerprints: [s.id],
                                  member_event_count: s.estimated_count,
                                });
                                toast.show('Schema saved.', { kind: 'success' });
                                // Force re-fetch to pick up matched_schema_id
                                getViewSuggestions(source.source_id).then(r => { setSuggestions(r.suggestions ?? []); setSampled(r.sampled ?? 0); });
                              } catch (e) {
                                toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
                              }
                            }}
                            style={{ fontSize: 11, padding: '3px 10px' }}
                          >★ save schema</button>
                        ) : (
                          <>
                            <button
                              className="btn sm ghost"
                              title="One-click: create a promote_to_table trigger so events matching this schema land in a typed BQ table. View auto-created from core paths."
                              onClick={async () => {
                                if (!s.matched_schema_id) return;
                                try {
                                  const r = await promoteSchemaToBQ(source.source_id, s.matched_schema_id);
                                  toast.show(`BQ trigger created · table ${r.table_name}`, { kind: 'success' });
                                } catch (e) {
                                  toast.show(`Promote failed: ${(e as Error).message.slice(0, 120)}`, { kind: 'error' });
                                }
                              }}
                              style={{ fontSize: 11, padding: '3px 10px', color: 'oklch(0.45 0.16 150)' }}
                            >→ BQ</button>
                            <button
                              className="btn sm ghost"
                              title="Archive this Schema Definition. New events stop matching it; existing events keep their fingerprints."
                              onClick={async () => {
                                if (!s.matched_schema_id) return;
                                if (!window.confirm(`Archive Schema "${s.matched_schema_name ?? 'untitled'}"? New events will no longer match it.`)) return;
                                try {
                                  await archiveSchemaDef(source.source_id, s.matched_schema_id);
                                  toast.show('Schema archived.', { kind: 'success' });
                                  getViewSuggestions(source.source_id).then(r => { setSuggestions(r.suggestions ?? []); setSampled(r.sampled ?? 0); });
                                } catch (e) {
                                  toast.show(`Archive failed: ${(e as Error).message}`, { kind: 'error' });
                                }
                              }}
                              style={{ fontSize: 11, padding: '3px 10px', color: 'var(--ink-3)' }}
                            >unsave</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        });
      })()}
      </>
      )}
    </div>
  );
}
